import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../../shared/Button";
import DateRangePicker from "../../../shared/DatePicker/DateRange";
import { DriverSelect } from "../../../components/DriverSelect";
import { useCreateManifestV2Mutation } from "../../../redux/services/manifests/manifestsV2Api";
import useForm from "../../../hooks/useForm";
import { useAuth } from "../../../hooks/useAuth";
import { useConfig } from "../../../hooks/useConfig";
import request, { renderQueries } from "../../../utils/request";
import { buildOperationalUtcWindow } from "../../../utils/date.utils";
import {
  buildManifestV2CreatePayload,
  COMPLIANCE_PROFILE_OPTIONS,
} from "../utils";
import Select from "../../../shared/Select";
import Toggle from "../../../shared/Toggle";
import ManifestV2LegPicker from "../components/ManifestV2LegPicker";
import { getProductTerminology } from "../../../constants/product-terminology";

const ManifestV2Create = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tripDraft = location.state?.tripDraft;
  const appliedTripDraftRef = useRef(false);
  const { user } = useAuth();
  const { configs, appType } = useConfig();
  const terms = getProductTerminology(appType);
  const { form, updateForm } = useForm();
  const [createManifestV2, { isLoading: creating }] = useCreateManifestV2Mutation();
  const [manualMode, setManualMode] = useState(false);
  const [selectedLegIds, setSelectedLegIds] = useState([]);
  const [dispatchDraftNotice, setDispatchDraftNotice] = useState("");

  const manualSelectionEnabled =
    configs?.features?.manifestsV2?.manualSelection?.enabled !== false;

  useEffect(() => {
    if (!tripDraft || appliedTripDraftRef.current) return;
    appliedTripDraftRef.current = true;

    if (tripDraft.startDate && tripDraft.endDate) {
      updateForm({
        startDate: tripDraft.startDate,
        endDate: tripDraft.endDate,
      });
    }

    if (tripDraft.driverId) {
      updateForm({
        id: "driver",
        value: { _id: tripDraft.driverId },
      });
    }

    if (tripDraft.manualSelection && tripDraft.legIds?.length) {
      setManualMode(true);
      setSelectedLegIds(tripDraft.legIds.map((id) => String(id)));
    }

    if (tripDraft.source === "dispatch") {
      setDispatchDraftNotice(
        tripDraft.driverId
          ? terms.dispatchDraftFromDispatchWithDriver
          : terms.dispatchDraftFromDispatchWithoutDriver
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripDraft]);

  const handleDateRangeChange = ([start, end]) => {
    updateForm((prevForm) => ({
      ...prevForm,
      startDate: start ? start.toISOString() : null,
      endDate: end ? end.toISOString() : null,
    }));
  };

  const refreshLegCount = async () => {
    const driverId = form.driver?._id || form.driver?.value?._id || form.driver;
    if (!driverId || !form.startDate || !form.endDate) return;

    const manifestWindow = buildOperationalUtcWindow(form.startDate, form.endDate);
    const query = {
      parent: user.parentCompany,
      driverId,
      manifestId: null,
      manifestV2Id: null,
      route: true,
      startDate: manifestWindow.startDate,
      endDate: manifestWindow.endDate,
      countOnly: true,
    };

    const res = await request(`/legs?${renderQueries(query)}`);
    updateForm({ id: "count", value: res.data.data || "0" });
  };

  useEffect(() => {
    if (!manualMode && form.startDate && form.endDate && form.driver) {
      refreshLegCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.startDate, form.endDate, form.driver, manualMode]);

  async function handleSubmit() {
    const manifestWindow = buildOperationalUtcWindow(form.startDate, form.endDate);
    const selectedDriver = form.driver?.value || form.driver;
    const payload = buildManifestV2CreatePayload({
      ...form,
      driver: selectedDriver?._id || selectedDriver,
      startDate: manifestWindow.startDate,
      endDate: manifestWindow.endDate,
      legIds: manualMode ? selectedLegIds : undefined,
      status: "planned",
    });

    try {
      const res = await createManifestV2(payload).unwrap();
      toast.success(res.message || "Manifest created");
      navigate(`/manifests/v2/${res.data._id}`);
    } catch (error) {
      toast.error(error?.data?.message || "Unable to create manifest");
    }
  }

  const selectedDriver = form.driver?.value || form.driver;
  const canSubmit =
    selectedDriver &&
    (manualMode
      ? selectedLegIds.length > 0
      : form.startDate && form.endDate && Number(form.count || 0) > 0) &&
    !creating;

  return (
    <div className="mx-auto max-w-2xl space-y-6 px-4 py-6 md:px-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">
          {terms.tripManifestTitle}
        </h1>
        <p className="mt-1 text-sm text-gray-500">{terms.tripManifestSubtitle}</p>
        {dispatchDraftNotice ? (
          <p className="mt-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-800">
            {dispatchDraftNotice}
          </p>
        ) : null}
      </div>

      <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <DriverSelect
          id="manifest-v2-driver"
          label="Driver"
          placeholder="Select driver"
          size="sm"
          value={selectedDriver?.fullName || ""}
          onChange={(option) =>
            updateForm({
              id: "driver",
              value: option?.value || option?.data || null,
            })
          }
        />

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Appointment date range
          </label>
          <DateRangePicker
            startDate={form.startDate ? new Date(form.startDate) : undefined}
            endDate={form.endDate ? new Date(form.endDate) : undefined}
            onChange={handleDateRangeChange}
          />
        </div>

        <div>
          <Select
            label="Compliance profile"
            placeholder="Select profile"
            options={COMPLIANCE_PROFILE_OPTIONS}
            value={form.complianceProfile}
            onChange={(option) =>
              updateForm({ id: "complianceProfile", value: option.value })
            }
          />
          <p className="mt-1 text-xs text-gray-500">
            {terms.tripManifestComplianceHint}
          </p>
        </div>

        {manualSelectionEnabled ? (
          <Toggle
            label="Choose legs manually"
            checked={manualMode}
            onChange={setManualMode}
          />
        ) : null}

        {manualMode ? (
          <ManifestV2LegPicker
            driverId={selectedDriver?._id || selectedDriver}
            parentCompany={user.parentCompany}
            startDate={form.startDate}
            endDate={form.endDate}
            selectedLegIds={selectedLegIds}
            onChange={setSelectedLegIds}
          />
        ) : (
          <div className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">
            Matching unmanifested legs: <strong>{form.count || 0}</strong>
            <p className="mt-1 text-xs text-gray-500">
              {terms.tripManifestAutoLegsHint}
            </p>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3">
        <Button type="alternative" onClick={() => navigate("/manifests/v2")}>
          Cancel
        </Button>
        <Button disabled={!canSubmit} loading={creating} onClick={handleSubmit}>
          {terms.tripManifestCreateAction}
        </Button>
      </div>
    </div>
  );
};

export default ManifestV2Create;
